import { Canvas, Rect, Textbox, util } from "fabric";
import type React from "react";

export const handleEditPdfText = (
  textItem: any,
  pdfOverlayFabricRef: React.RefObject<Canvas | null>,
  hideContextMenu: () => void
) => {
  hideContextMenu?.();
  const canvas = pdfOverlayFabricRef?.current;
  if (!canvas) return;
  const PADDING_X = 2;
  const PADDING_Y = 2;
  const mask = new Rect({
    left: textItem.x - PADDING_X,
    top: textItem.y - textItem.height - PADDING_Y,
    width: textItem.width + PADDING_X * 2,
    height: textItem.height + PADDING_Y * 2,
    fill: "white",
    selectable: true,
    evented: true,
    pdfMeta: { type: "text-mask" },
  } as any);
  const textBox = new Textbox(textItem.str, {
    left: textItem.x,
    top: textItem.y - textItem.height,
    fontSize: textItem.fontSize,
    width: textItem.width * 1.1,
    fill: "black",
    fontFamily: textItem.fontName || "Arial",
    hasBorders: true,
    hasControls: true,
    borderColor: "blue",
    cornerColor: "blue",
    pdfMeta: { type: "text-replacement" },
  } as any);
  canvas.add(mask);
  canvas.add(textBox);
  canvas.setActiveObject(textBox);
  textBox.enterEditing();
  textBox.selectAll();
  canvas.renderAll();
};

export const handleMaskPdfText = (
  textItem: any,
  pdfOverlayFabricRef: React.RefObject<Canvas | null>,
  hideContextMenu: () => void
) => {
  hideContextMenu?.();
  const canvas = pdfOverlayFabricRef?.current;
  if (!canvas) return;
  const rect = new Rect({
    left: textItem.x,
    top: textItem.y - textItem.height,
    width: textItem.width,
    height: textItem.height,
    fill: "white",
    selectable: true,
  });
  canvas.add(rect);
  canvas.renderAll();
};

export const handleReplacePdfText = (
  textItem: any,
  pdfOverlayFabricRef: React.RefObject<Canvas | null>,
  hideContextMenu: () => void
) => {
  hideContextMenu();
  const canvas = pdfOverlayFabricRef?.current;
  if (!canvas) return;
  const PADDING_X = 2;
  const PADDING_Y = 2;
  const mask = new Rect({
    left: textItem.x - PADDING_X,
    top: textItem.y - textItem.height - PADDING_Y,
    width: textItem.width + PADDING_X * 2,
    height: textItem.height + PADDING_Y * 2,
    fill: "white",
    selectable: true,
    evented: true,
    pdfMeta: { type: "text-mask" },
  } as any);
  const textBox = new Textbox(textItem.str, {
    left: textItem.x,
    top: textItem.y - textItem.height,
    fontSize: textItem.fontSize,
    fill: "black",
    fontFamily: textItem.fontName || "Arial",
    width: textItem.width,
    hasBorders: true,
    hasControls: true,
    borderColor: "#3b82f6",
    cornerColor: "#3b82f6",
    pdfMeta: { type: "text-replacement" },
  } as any);
  canvas.add(mask);
  canvas.add(textBox);
  canvas.renderAll();
};

export const getOriginalPdfBytes = async (
  originalPdfSource: string | ArrayBuffer | null
): Promise<ArrayBuffer> => {
  if (!originalPdfSource) {
    throw new Error("No PDF source available");
  }
  if (originalPdfSource instanceof ArrayBuffer) {
    return originalPdfSource;
  }
  if (typeof originalPdfSource === "string") {
    const response = await fetch(originalPdfSource);
    if (!response.ok) throw new Error("Failed to fetch pdf");
    return await response.arrayBuffer();
  }
  throw new Error("Invalid PDF source type");
};

export const serializeCanvasForPage = (canvas: Canvas | null): string | null => {
  if (!canvas) {
    return null;
  }
  const objectsToSave = canvas
    .getObjects()
    .filter((obj: any) => !obj.isHitbox && obj.pdfMeta?.type !== "text");
  if (objectsToSave.length === 0) {
    return JSON.stringify({ version: "6.0.0", objects: [] });
  }
  const canvasData = {
    version: "6.0.0",
    objects: objectsToSave.map((obj: any) => {
      if (!obj.pdfMeta) {
        obj.pdfMeta = { type: obj.type || "unknown" };
      }
      return obj.toJSON(["pdfMeta"]);
    }),
  };
  const json = JSON.stringify(canvasData);
  return json;
};

export const clearNonTextObjects = (canvas: Canvas) => {
  const objectsToRemove = canvas
    .getObjects()
    .filter((obj: any) => !obj.isHitbox && obj.pdfMeta?.type !== "text");
  objectsToRemove.forEach((obj) => canvas.remove(obj));
};

export const loadPageState = async (
  pageKey: string | number,
  pdfOverlayFabricRef: React.RefObject<Canvas | null>,
  pageCanvasData: Record<string, string>
): Promise<void> => {
  const canvas = pdfOverlayFabricRef.current;
  if (!canvas) {
    return;
  }
  const savedData = pageCanvasData[pageKey as any];
  clearNonTextObjects(canvas);
  if (!savedData) {
    canvas.renderAll();
    return;
  }
  try {
    const data = JSON.parse(savedData);
    if (!data || !data.objects || data.objects.length === 0) {
      canvas.renderAll();
      return;
    }
    const enlivenedObjects = await util.enlivenObjects(data.objects);
    enlivenedObjects.forEach((obj: any) => {
      canvas.add(obj);
    });
    const hitboxes = canvas
      .getObjects()
      .filter((o: any) => o.isHitbox || o.pdfMeta?.type === "text");
    hitboxes.forEach((box) => canvas.sendObjectToBack(box));
    canvas.renderAll();
  } catch {
    try {
      await canvas.loadFromJSON(savedData);
      const badHitboxes = canvas
        .getObjects()
        .filter((o: any) => o.type === "pdf-text" && !o.pdfMeta);
      badHitboxes.forEach((o) => canvas.remove(o));
      canvas.renderAll();
    } catch {
      canvas.renderAll();
    }
  }
};

export const waitForCanvasReady = async (
  timeout = 1000,
  interval = 50,
  pdfOverlayFabricRef: React.RefObject<Canvas | null>,
  pdfSize: { width: number; height: number }
) => {
  const start = Date.now();
  return new Promise<void>((resolve, reject) => {
    const check = () => {
      const canvas = pdfOverlayFabricRef.current;
      if (canvas && pdfSize.width && pdfSize.height) {
        return resolve();
      }
      if (Date.now() - start > timeout) {
        return reject(new Error("Canvas not ready (timeout)"));
      }
      setTimeout(check, interval);
    };
    check();
  });
};

export const extractPdfText = async (page: any) => {
  const textContent = await page.getTextContent();
  return textContent.items.map((item: any) => ({
    str: item.str,
    x: item.transform[4],
    y: page.getViewport({ scale: 1 }).height - item.transform[5],
    width: item.width,
    height: item.height,
    fontSize: item.transform[0],
  }));
};
